import type { ReactNode } from 'react';
import { Badge } from '@paige/ui';
import { useFormat } from '@paige/format';
import type { HealthTicket, HealthTickets } from './health-types';
import { openSeconds, ticketTone, ticketWord } from './health-words';

/** The day's tickets against the cap, one pip each: filled for one opened in the last 24 hours. */
function CapPips({ tickets }: { tickets: HealthTickets }): ReactNode {
  const f = useFormat();
  if (tickets.on === false) {
    return <p className="hl-cap">Desk tickets are switched off; alerts only mail.</p>;
  }
  const cap = tickets.perDay;
  if (cap === null) return null;
  const used = tickets.openedLastDay;
  return (
    <p className="hl-cap">
      <span className="hl-pips" aria-hidden="true">
        {Array.from({ length: Math.max(cap, used) }, (_, i) => (
          <span key={i} className={i < used ? 'hl-pip hl-pip--used' : 'hl-pip'} />
        ))}
      </span>
      <span>
        <strong>{f.int(used)}</strong> of {f.int(cap)} a day opened in the last 24 hours
      </span>
    </p>
  );
}

function TicketRow({ ticket, now }: { ticket: HealthTicket; now: number }): ReactNode {
  const f = useFormat();
  const overflow = ticket.kind === 'overflow';
  return (
    <li className={overflow ? 'hl-ticket hl-ticket--overflow' : 'hl-ticket'}>
      <code className="hl-ticket__number">
        {overflow ? 'rides on' : ticket.number ? `#${ticket.number}` : 'no number'}
      </code>
      <div className="hl-ticket__body">
        <p className="hl-alert__title" title={ticket.alertKey}>
          {ticket.title ?? ticket.alertKey}
        </p>
        <p className="hl-alert__when">
          <span title={f.dateTime(ticket.createdAt)}>opened {f.ago(ticket.createdAt, now)}</span>
          {ticket.closedAt ? (
            <span title={f.dateTime(ticket.closedAt)}>
              closed after {f.duration(openSeconds(ticket.createdAt, ticket.closedAt))}
            </span>
          ) : null}
        </p>
      </div>
      <Badge tone={ticketTone[ticket.state]}>{ticketWord[ticket.state]}</Badge>
    </li>
  );
}

/**
 * The Desk tickets the health monitor opened in the last 14 days, newest first: one per incident, closed by the
 * monitor when its alert clears, at most the day's cap (an alert past it rides on an open ticket), and none for
 * Ademero's test companies.
 */
export function TicketsPanel({ tickets, now }: { tickets: HealthTickets; now: number }): ReactNode {
  return (
    <>
      <CapPips tickets={tickets} />
      {tickets.list.length === 0 ? (
        <p className="pl-part__quiet">No ticket in the last 14 days.</p>
      ) : (
        <ul className="hl-tickets" aria-label="Desk tickets">
          {tickets.list.map((t) => (
            <TicketRow key={`${t.alertKey}-${t.createdAt}`} ticket={t} now={now} />
          ))}
        </ul>
      )}
    </>
  );
}
